import React, { useMemo, useState } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { selectRootAccounts } from 'rootSlice'
import { getAxiosDefaultConfig } from 'utils'
import { isStoreLessAccount, isWooCommerceChannel } from 'modules/storeTools'
import {
  baseUrl,
  disconnectWoocommerceSiteUrl,
  transferWoocommerceSiteUrl,
} from 'urls'

type OrgRow = { id: string; name: string }

interface WooCommerceUninstallModalProps {
  visible: boolean
  closeModal: () => void
  integration?: {
    channel?: string
    id?: string
  } | null
}

export default function WooCommerceUninstallModal({
  visible,
  closeModal,
  integration,
}: WooCommerceUninstallModalProps) {
  const isWordpressCard = integration?.id === 'wordpress'
  const label = isWordpressCard ? 'WordPress' : 'WooCommerce'
  const accounts = useSelector(selectRootAccounts) || []
  const siteAccounts = useMemo(
    () =>
      accounts.filter((a) => {
        if (!isWooCommerceChannel(a)) return false
        return isWordpressCard ? isStoreLessAccount(a) : !isStoreLessAccount(a)
      }),
    [accounts, isWordpressCard],
  )

  const [isProcessing, setIsProcessing] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [organizations, setOrganizations] = useState<OrgRow[]>([])
  const [transferOrgId, setTransferOrgId] = useState('')
  const [processedStoreIds, setProcessedStoreIds] = useState<string[]>([])

  React.useEffect(() => {
    if (!visible) {
      setCompleted(false)
      setShowError(false)
      setErrorMessage('')
      setTransferOrgId('')
      setProcessedStoreIds([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await axios.get(`${baseUrl}/organizations`, getAxiosDefaultConfig())
        const data = res.data
        const list = Array.isArray(data?.organizations) ? data.organizations : Array.isArray(data) ? data : []
        if (!cancelled) {
          setOrganizations(
            list.map((o) => ({ id: String(o.id), name: String(o.name || o.display_name || 'Organization') })),
          )
        }
      } catch {
        /* optional */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [visible])

  const handleDisconnectAll = async () => {
    setIsProcessing(true)
    setShowError(false)
    const newlyProcessed: string[] = []
    try {
      if (!siteAccounts.length) {
        throw new Error(`No ${label} sites found on this account`)
      }
      for (const account of siteAccounts) {
        const storeId = String(account.merchant_id || account.domain || account.id)
        if (processedStoreIds.includes(storeId)) {
          continue
        }
        await axios.delete(
          `${disconnectWoocommerceSiteUrl}?store_id=${encodeURIComponent(storeId)}`,
          getAxiosDefaultConfig(),
        )
        newlyProcessed.push(storeId)
      }
      setCompleted(true)
      toast.success(`${label} site(s) disconnected`)
    } catch (err) {
      console.error(err)
      setShowError(true)
      setErrorMessage(err?.response?.data?.error || err?.message || 'Disconnect failed')
    } finally {
      setProcessedStoreIds((prev) => [...prev, ...newlyProcessed])
      setIsProcessing(false)
    }
  }

  const handleTransfer = async () => {
    if (!transferOrgId || !siteAccounts.length) return
    setIsProcessing(true)
    setShowError(false)
    const newlyProcessed: string[] = []
    try {
      for (const account of siteAccounts) {
        const storeId = String(account.merchant_id || account.domain || account.id)
        if (processedStoreIds.includes(storeId)) {
          continue
        }
        await axios.post(
          transferWoocommerceSiteUrl,
          { store_id: storeId, organization_id: transferOrgId },
          getAxiosDefaultConfig(),
        )
        newlyProcessed.push(storeId)
      }
      setCompleted(true)
      toast.success('Site(s) transferred to organization')
    } catch (err) {
      console.error(err)
      setShowError(true)
      setErrorMessage(err?.response?.data?.error || err?.message || 'Transfer failed')
    } finally {
      setProcessedStoreIds((prev) => [...prev, ...newlyProcessed])
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    closeModal()
    if (completed) window.location.reload()
  }

  return (
    <Modal show={visible} onHide={handleClose}>
      <Modal.Body>
        <div className="tw-p-6">
          <h2 className="tw-text-xl tw-mb-6">Disconnect {label}</h2>
          {showError && (
            <p className="tw-text-red-600 tw-mb-4">{errorMessage || 'Error updating integration.'}</p>
          )}
          <p className="tw-mb-4">
            {completed ? (
              'Done. You can reconnect from the WordPress plugin when ready.'
            ) : isWordpressCard ? (
              <>
                Disconnect clears PayHelm ownership of your WordPress site(s) so they can be claimed
                again from the plugin (including into an organization). This does not uninstall the
                plugin. WooCommerce stores are managed on the WooCommerce card.
              </>
            ) : (
              <>
                Disconnect clears PayHelm ownership of your WooCommerce site(s) so they can be claimed
                again from the plugin (including into an organization). This does not uninstall the
                WordPress plugin. WordPress-only (no WooCommerce) sites are managed on the WordPress
                card.
              </>
            )}
          </p>
          {!completed && siteAccounts.length > 0 && (
            <ul className="tw-mb-4 tw-text-sm tw-text-slate-600">
              {siteAccounts.map((a) => (
                <li key={String(a.merchant_id || a.domain || a.id)}>
                  {a.domain || a.merchant_id || a.id}
                </li>
              ))}
            </ul>
          )}
          {!completed && organizations.length > 0 && (
            <div className="tw-mb-4">
              <label className="tw-block tw-text-sm tw-mb-1">Or transfer to an organization</label>
              <select
                className="tw-w-full tw-border tw-rounded tw-px-2 tw-py-1"
                value={transferOrgId}
                onChange={(e) => setTransferOrgId(e.target.value)}
              >
                <option value="">Select organization…</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="tw-flex tw-justify-end tw-space-x-2">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              {completed ? 'Done' : 'Cancel'}
            </Button>
            {!completed && transferOrgId && (
              <Button variant="primary" size="sm" onClick={handleTransfer} disabled={isProcessing}>
                {isProcessing ? <Spinner animation="border" size="sm" /> : 'Transfer'}
              </Button>
            )}
            {!completed && (
              <Button variant="danger" size="sm" onClick={handleDisconnectAll} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <Spinner animation="border" size="sm" className="tw-mr-2" />
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect'
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
